import React, { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/lib/axiosInstance'
import type { PropertyListings } from '@/types/PropertyListings'
import Pagination from '@/components/Pagination'
import { motion } from 'framer-motion';
import ActiveListingTable from './listingTable/ActiveListingTable'
import ExpiredListingTable from './listingTable/ExpiredListingTable'
import PendingListingTable from './listingTable/PendingListingTable'
import Helper from '@/lib/helper'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import CoShareListingTable from './listingTable/CoShareListingTable'
import { useSelector } from 'react-redux'
import type { RootState } from '@/lib/redux/store'
import CoAgencyListingTable from './listingTable/CoAgencyListingTable'
import ClosedListingTable from './listingTable/ClosedListingTable'
import DraftListingTable from './listingTable/DraftListingTable'
import { isUnfinished, useAIConversations } from '@/components/listings/ai/useAIConversations'
import { IntelligenceButton } from '@/components/ui/intelligence'
import { Input } from '@/components/ui/input'
import { Search, X } from 'lucide-react'
import DeactivatedListingTable from './listingTable/DeactivatedListingTable'

interface ListingCounts {
    active: number;
    draft: number;
    pending: number;
    expired: number;
    closed: number;
    'co-share': number;
    'co-agency': number;
    deactivated: number;
}

type ActionType = 'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost';

const ACTION_CONFIG: Record<ActionType, {
    title: string;
    description: string;
    confirmLabel: string;
    confirmVariant: 'default' | 'destructive' | 'outline';
}> = {
    delete: {
        title: 'Are you sure you want to delete this listing?',
        description: 'This action cannot be undone. This will permanently delete your listing.',
        confirmLabel: 'Delete',
        confirmVariant: 'destructive',
    },
    deactivate: {
        title: 'Deactivate this listing?',
        description: 'This listing will be hidden from the public. You can reactivate it later.',
        confirmLabel: 'Deactivate',
        confirmVariant: 'default',
    },
    close: {
        title: 'Mark this listing as closed?',
        description: 'This will mark the listing as a done deal. You can reopen it later if needed.',
        confirmLabel: 'Mark as Closed',
        confirmVariant: 'default',
    },
    reopen: {
        title: 'Reopen this listing?',
        description: 'This will reopen the listing and refresh it for another month.',
        confirmLabel: 'Reopen',
        confirmVariant: 'default',
    },
    activate: {
        title: 'Activate this listing?',
        description: 'This will reopen the listing and make it active again.',
        confirmLabel: 'Activate',
        confirmVariant: 'default',
    },
    repost: {
        title: 'Repost this listing?',
        description: 'This will make the listing live again with a fresh 1-month expiry.',
        confirmLabel: 'Repost',
        confirmVariant: 'default',
    }
};

const Listings = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    let menu = agent.isInternal === 1
        ? ['active', 'draft', 'pending', 'expired', 'closed', 'co-share', 'co-agency', 'deactivated']
        : ['active', 'draft', 'expired', 'closed', 'co-agency', 'deactivated'];

    const [page, setPage] = useState(1);
    const { sessions: aiConversations } = useAIConversations();
    const openConversations = aiConversations.filter(isUnfinished);
    const [length, setLength] = useState(10);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [active, setActive] = useState('active');

    const [listings, setListings] = useState<PropertyListings[] | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState<ListingCounts>({
        active: 0,
        draft: 0,
        pending: 0,
        expired: 0,
        closed: 0,
        'co-share': 0,
        'co-agency': 0,
        deactivated: 0
    });

    const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [actionType, setActionType] = useState<ActionType>('delete');
    const [confirming, setConfirming] = useState<boolean>(false);

    // ── Bulk deactivate (active tab) ───────────────────────────────
    const [bulkDeactivateMode, setBulkDeactivateMode]         = useState(false);
    const [bulkDeactivateIds, setBulkDeactivateIds]           = useState<number[]>([]);
    const [bulkDeactivateOpen, setBulkDeactivateOpen]         = useState(false);
    const [bulkDeactivateConfirming, setBulkDeactivateConfirming] = useState(false);

    // ── Bulk reactivate (deactivated tab) ─────────────────────────
    const [bulkReactivateMode, setBulkReactivateMode]         = useState(false);
    const [bulkReactivateIds, setBulkReactivateIds]           = useState<number[]>([]);
    const [bulkReactivateOpen, setBulkReactivateOpen]         = useState(false);
    const [bulkReactivateConfirming, setBulkReactivateConfirming] = useState(false);

    const selectedDeactivateListings  = listings?.filter(l => bulkDeactivateIds.includes(l.id))  ?? [];
    const selectedReactivateListings  = listings?.filter(l => bulkReactivateIds.includes(l.id))  ?? [];

    // ── Deactivate bulk helpers ────────────────────────────────────
    const toggleDeactivateId = (id: number) =>
        setBulkDeactivateIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

    const toggleDeactivateAll = () => {
        if (!listings) return;
        const allIds = listings.map(l => l.id);
        setBulkDeactivateIds(allIds.every(id => bulkDeactivateIds.includes(id)) ? [] : allIds);
    };

    const toggleReactivateId = (id: number) =>
        setBulkReactivateIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

    const toggleReactivateAll = () => {
        if (!listings) return;
        const allIds = listings.map(l => l.id);
        setBulkReactivateIds(allIds.every(id => bulkReactivateIds.includes(id)) ? [] : allIds);
    };

    // Reset selection when tab changes
    useEffect(() => {
        setBulkDeactivateIds([]);
        setBulkDeactivateMode(false);
        setBulkReactivateIds([]);
        setBulkReactivateMode(false);
    }, [active]);

    const getCounts = async () => {
        try {
            const response = await api.get('/listings?countsOnly=true');
            if (!response.data.status) throw new Error(response.data.error);
            setCounts(response.data.counts);
        } catch (err: any) {
            console.error('Error fetching counts:', err);
        }
    };

    const getListings = async (typeOverride?: string, pageOverride?: number, lengthOverride?: number, searchOverride?: string) => {
        try {
            setLoading(true);
            const finalType = typeOverride ?? active;
            const finalPage = pageOverride ?? page;
            const finalLength = lengthOverride ?? length;
            const finalSearch = searchOverride ?? search;
            const response = await api.get(
                `/listings?page=${finalPage}&length=${finalLength}&type=${finalType}${finalSearch ? `&search=${finalSearch}` : ''}`
            );
            if (!response.data.status) throw new Error(response.data.error);
            setListings(response.data.listings);
            setTotal(response.data.total);
        } catch (err: any) {
            toast.error(err.response?.data.error ?? err.message ?? 'Something went wrong');
        } finally {
            setLoading(false);
            setOpen(false);
            setSelectedListingId(null);
        }
    };

    const handleConfirm = async () => {
        if (!selectedListingId) return;
        try {
            setConfirming(true);
            let response;
            if (actionType === 'delete') {
                response = await api.delete(`/listings/${selectedListingId}`);
            } else {
                response = await api.post(`/listings/${selectedListingId}/${actionType}`);
            }
            if (!response.data.status) throw new Error(response.data.message ?? response.data.error ?? 'Action failed.');
            toast.success(ACTION_CONFIG[actionType].confirmLabel + ' successful.');
            getListings();
            getCounts();
        } catch (err: any) {
            toast.error(err.response?.data?.message ?? err.message);
        } finally {
            setConfirming(false);
        }
    };

    const handleBulkDeactivateConfirm = async () => {
        if (bulkDeactivateIds.length === 0) return;
        try {
            setBulkDeactivateConfirming(true);
            const response = await api.post('/listings/deactivate/bulk', { 
                listingIds: bulkDeactivateIds 
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.data.status) throw new Error(response.data.error);
            const { results } = response.data;
            if (results.failed.length > 0) {
                toast.warning(`${results.success.length} deactivated, ${results.failed.length} failed.`);
            } else {
                toast.success(response.data.message);
            }
            setBulkDeactivateIds([]);
            setBulkDeactivateOpen(false);
            setBulkDeactivateMode(false);
            getListings();
            getCounts();
        } catch (err: any) {
            toast.error(err.response?.data?.message ?? err.message ?? 'Something went wrong.');
        } finally {
            setBulkDeactivateConfirming(false);
        }
    };

    const handleBulkReactivateConfirm = async () => {
        if (bulkReactivateIds.length === 0) return;
        try {
            setBulkReactivateConfirming(true);
            const response = await api.post('/listings/reactivate/bulk', { 
                listingIds: bulkReactivateIds 
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            const { results } = response.data;
            if (results?.failed?.length > 0) {
                toast.warning(`${results.success.length} reactivated, ${results.failed.length} failed.`);
            } else {
                toast.success(response.data.message ?? 'Listings reactivated successfully.');
            }
            setBulkReactivateIds([]);
            setBulkReactivateOpen(false);
            setBulkReactivateMode(false);
            getListings();
            getCounts();
        } catch (err: any) {
            toast.error(err.response?.data?.message ?? err.message ?? 'Something went wrong.');
        } finally {
            setBulkReactivateConfirming(false);
        }
    };

    const handleTabClick = (tab: string) => {
        const params: Record<string, string> = { 
            type: tab, 
            page: '1', 
            length: tab === 'co-share' ? '12' : '10'
        };
        if (search) params.search = search;
        setSearchParams(params);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchParams({ type: active, page: '1', length: length.toString() });
    };

    const handlePageChange = (newPage: number) => {
        const params: Record<string, string> = { type: active, page: newPage.toString(), length: length.toString() };
        if (search) params.search = search;
        setSearchParams(params);
    };

    useEffect(() => {
        if (searchInput === search) return;
        const debounceTimer = setTimeout(() => {
            const params: Record<string, string> = { type: active, page: '1', length: length.toString() };
            if (searchInput.trim()) params.search = searchInput.trim();
            setSearchParams(params);
        }, 500);
        return () => clearTimeout(debounceTimer);
    }, [searchInput]);

    useEffect(() => {
        const typeFromUrl = searchParams.get('type')?.toLowerCase();
        const pageFromUrl = searchParams.get('page')   ?? '1';
        const lengthFromUrl = searchParams.get('length') ?? '10';
        const searchFromUrl = searchParams.get('search') ?? '';

        const finalType = menu.includes(typeFromUrl ?? '') ? typeFromUrl! : 'active';
        const finalPage = Number(pageFromUrl);
        const finalLength = Number(lengthFromUrl);

        setActive(finalType);
        setPage(finalPage);
        setLength(finalLength);
        setSearch(searchFromUrl);
        if (searchInput !== searchFromUrl) setSearchInput(searchFromUrl);

        getListings(finalType, finalPage, finalLength, searchFromUrl);
        getCounts();
    }, [searchParams, agent]);

    const currentAction = ACTION_CONFIG[actionType];

    return (
        <div>
            <div className='mb-5 flex flex-wrap items-center justify-between gap-3'>
                <h5 className='h5 uppercase bold'>Listings</h5>
                <div className='flex items-center gap-2'>
                    {/* Straight into the AI, without picking a method first. */}
                    <IntelligenceButton size='sm' filled onClick={() => navigate('/listings/add?mode=ai')} ariaLabel='Create a listing with AI' />
                    <Button asChild size={'sm'} variant={'default'}>
                        <Link className='!text-white' to={'/listings/add'}>NEW</Link>
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className='mb-5'>
                <div className='relative max-w-md'>
                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={18} />
                    <Input
                        type="text"
                        placeholder="Search by listing reference number..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-10 pr-10"
                    />
                    {searchInput && (
                        <Button
                            variant="ghost" size="sm"
                            onClick={handleClearSearch}
                            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                        >
                            <X size={16} />
                        </Button>
                    )}
                </div>
                {searchInput && (
                    <p className='text-xs text-muted-foreground mt-1'>Searching for "{searchInput}"...</p>
                )}
            </div>

            {active === 'active' && (
                <div className='flex justify-end items-center gap-2 py-2'>
                    <Button
                        size='sm'
                        variant={bulkDeactivateMode ? 'default' : 'outline'}

                        onClick={() => { setBulkDeactivateMode(prev => !prev); setBulkDeactivateIds([]); }}
                    >
                        {bulkDeactivateMode ? 'Cancel' : 'Bulk Deactivate'}
                    </Button>
                    {bulkDeactivateMode && bulkDeactivateIds.length > 0 && (
                        <Button
                            size='sm' variant='destructive'
                            onClick={() => setBulkDeactivateOpen(true)}
                        >
                            Deactivate {bulkDeactivateIds.length} selected
                        </Button>
                    )}
                </div>
            )}

            {active === 'deactivated' && (
                <div className='flex justify-end items-center gap-2 py-2'>
                    <Button
                        size='sm'
                        variant={bulkReactivateMode ? 'default' : 'outline'}

                        onClick={() => { setBulkReactivateMode(prev => !prev); setBulkReactivateIds([]); }}
                    >
                        {bulkReactivateMode ? 'Cancel' : 'Bulk Reactivate'}
                    </Button>
                    {bulkReactivateMode && bulkReactivateIds.length > 0 && (
                        <Button
                        size='sm'
                        variant='outline'

                        onClick={() => setBulkReactivateOpen(true)}
                        >
                            Reactivate {bulkReactivateIds.length} selected
                        </Button>
                    )}
                </div>
            )}

            <div className='md:flex'>
                {menu.map(item => (
                    <div
                        key={item}
                        onClick={() => handleTabClick(item)}
                        className={`relative z-10 md:mx-3 md:px-3 mx-1 px-1 py-2 transition-colors cursor-pointer ${
                            active === item ? 'text-black font-bold' : 'text-gray-600'
                        }`}
                    >
                        <span className={`relative z-10 uppercase text-xs ${active === item ? 'text-black' : 'text-gray-400'} flex items-center gap-2`}>
                            {item}
                            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${
                                active === item ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                {counts[item as keyof ListingCounts] + (item === 'draft' ? openConversations.length : 0)}
                            </span>
                        </span>
                        {active === item && (
                            <motion.div
                                layoutId="underline"
                                className="absolute left-0 right-0 -bottom-1 h-[2px] bg-black"
                                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                            />
                        )}
                    </div>
                ))}
            </div>

            {active === 'active' ? (
                <ActiveListingTable
                    loading={loading} listings={listings} startIndex={(page - 1) * length}
                    setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType}
                    bulkMode={bulkDeactivateMode} selectedIds={bulkDeactivateIds}
                    onToggleSelect={toggleDeactivateId} onSelectAll={toggleDeactivateAll}
                    onBulkDeactivate={() => setBulkDeactivateOpen(true)}
                />
            ) : active === 'draft' ? (
                <DraftListingTable
                    loading={loading} listings={listings} startIndex={(page - 1) * length}
                    setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType}
                    // Unfinished AI conversations lead the first page; they are not paginated.
                    conversations={page === 1 && !search ? openConversations : []}
                />
            ) : active === 'pending' ? (
                <PendingListingTable loading={loading} listings={listings} startIndex={(page - 1) * length} setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType} />
            ) : active === 'expired' ? (
                <ExpiredListingTable loading={loading} listings={listings} startIndex={(page - 1) * length} setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType} />
            ) : active === 'closed' ? (
                <ClosedListingTable loading={loading} listings={listings} startIndex={(page - 1) * length} setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType} />
            ) : active === 'deactivated' ? (
                <DeactivatedListingTable
                    loading={loading} listings={listings} startIndex={(page - 1) * length}
                    setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType}
                    bulkMode={bulkReactivateMode} selectedIds={bulkReactivateIds}
                    onToggleSelect={toggleReactivateId} onSelectAll={toggleReactivateAll}
                    onBulkReactivate={() => setBulkReactivateOpen(true)}
                />
            ) : active === 'co-share' ? (
                <CoShareListingTable loading={loading} listings={listings} getListings={getListings} setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType} />
            ) : active === 'co-agency' && (
                <CoAgencyListingTable loading={loading} listings={listings} startIndex={(page - 1) * length} getListings={getListings} setOpen={setOpen} setSelectedListingId={setSelectedListingId} setActionType={setActionType} />
            )}

            <div className='flex justify-end'>
                <Pagination totalPage={Math.ceil(total / length)} currentPage={page} onPageChange={handlePageChange} />
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{currentAction.title}</DialogTitle>
                        <DialogDescription>{currentAction.description}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button size='sm' variant='outline' disabled={confirming}
                            onClick={(e) => { e.preventDefault(); setOpen(false); setSelectedListingId(null); }}>
                            Cancel
                        </Button>
                        <Button size='sm' variant={currentAction.confirmVariant} disabled={confirming}
                            onClick={(e) => { e.preventDefault(); handleConfirm(); }}>
                            {currentAction.confirmLabel}
                            {confirming && <Spinner className='ml-2' />}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk deactivate confirmation dialog */}
            <Dialog open={bulkDeactivateOpen} onOpenChange={setBulkDeactivateOpen}>
                <DialogContent className='max-w-lg'>
                    <DialogHeader>
                        <DialogTitle>Deactivate {selectedDeactivateListings.length} listing(s)?</DialogTitle>
                        <DialogDescription>
                            The following listings will be hidden from the public. You can reactivate them later.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='max-h-72 overflow-y-auto rounded-md border divide-y text-sm'>
                        {selectedDeactivateListings.map(listing => {
                            const coverImage =
                                listing.medias?.find(x => x.mediaType === 1 && 'isCover' in x && x.isCover === 1)?.path ||
                                listing.medias?.find(x => x.mediaType === 1)?.path
                            return (
                                <div key={listing.id} className='flex items-center gap-3 px-4 py-3'>
                                    <div className='w-14 h-14 rounded-md flex-shrink-0 bg-muted'
                                        style={coverImage ? { background: `url(${import.meta.env.VITE_AWS_S3}/${coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                                    />
                                    <div className='min-w-0 flex-1 space-y-0.5'>
                                        <p className='font-medium truncate'>{listing.headline}</p>
                                        <p className='text-muted-foreground text-xs truncate'>{listing.listingReferenceNumber} &middot; {listing.address}</p>
                                        <p className='text-xs font-semibold'>{listing.price ? `RM ${Number(listing.price).toLocaleString()}` : 'Price not set'}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <DialogFooter>
                        <Button size='sm' variant='outline' disabled={bulkDeactivateConfirming}
                            onClick={() => setBulkDeactivateOpen(false)}>Cancel</Button>
                        <Button size='sm' variant='destructive' disabled={bulkDeactivateConfirming}
                            onClick={handleBulkDeactivateConfirm}>
                            Deactivate all
                            {bulkDeactivateConfirming && <Spinner className='ml-2' />}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk reactivate confirmation dialog */}
            <Dialog open={bulkReactivateOpen} onOpenChange={setBulkReactivateOpen}>
                <DialogContent className='max-w-lg'>
                    <DialogHeader>
                        <DialogTitle>Reactivate {selectedReactivateListings.length} listing(s)?</DialogTitle>
                        <DialogDescription>
                            The following listings will be made visible to the public again.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='max-h-72 overflow-y-auto rounded-md border divide-y text-sm'>
                        {selectedReactivateListings.map(listing => {
                            const coverImage =
                                listing.medias?.find(x => x.mediaType === 1 && 'isCover' in x && x.isCover === 1)?.path ||
                                listing.medias?.find(x => x.mediaType === 1)?.path
                            return (
                                <div key={listing.id} className='flex items-center gap-3 px-4 py-3'>
                                    <div className='w-14 h-14 rounded-md flex-shrink-0 bg-muted'
                                        style={coverImage ? { background: `url(${import.meta.env.VITE_AWS_S3}/${coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                                    />
                                    <div className='min-w-0 flex-1 space-y-0.5'>
                                        <p className='font-medium truncate'>{listing.headline}</p>
                                        <p className='text-muted-foreground text-xs truncate'>{listing.listingReferenceNumber} &middot; {listing.address}</p>
                                        <p className='text-xs font-semibold'>{listing.price ? `RM ${Number(listing.price).toLocaleString()}` : 'Price not set'}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <DialogFooter>
                        <Button size='sm' variant='outline' disabled={bulkReactivateConfirming}
                            onClick={() => setBulkReactivateOpen(false)}>Cancel</Button>
                        <Button size='sm' className='text-white' disabled={bulkReactivateConfirming}
                            onClick={handleBulkReactivateConfirm}>
                            Reactivate all
                            {bulkReactivateConfirming && <Spinner className='ml-2' />}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Listings;
